import { useState, useEffect } from 'react';
import { HlsPlayer } from './HlsPlayer';
import { AudioBlock } from './AudioBlock';

interface Props {
  url: string;
}

type MediaKind = 'image' | 'video' | 'audio' | 'unknown' | 'loading';

export function StorageMedia({ url }: Props) {
  const [kind, setKind] = useState<MediaKind>('loading');
  const [hlsUrl, setHlsUrl] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function detect() {
      try {
        const res = await fetch(url, { method: 'HEAD' });
        const ct = res.headers.get('content-type') || '';
        // Storage-API sets X-HLS-URL when the transcoding pipeline has
        // produced a master.m3u8 (only ~28% of videos as of 2026-05;
        // historical uploads pre-pipeline have no HLS variant and
        // fall through to progressive MP4 download with range requests).
        // Storage-API confirmed in IACP a06a76c5: header is canonical,
        // do not construct the URL ourselves.
        const hls = res.headers.get('x-hls-url');

        if (cancelled) return;

        if (ct.startsWith('image/')) {
          setKind('image');
        } else if (ct.startsWith('audio/')) {
          setKind('audio');
        } else if (ct.startsWith('video/')) {
          setKind('video');
          if (hls) setHlsUrl(hls);
        } else {
          setKind('unknown');
        }
      } catch {
        if (!cancelled) setKind('unknown');
      }
    }

    detect();
    return () => { cancelled = true; };
  }, [url]);

  if (kind === 'loading') {
    return <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>Loading media...</span>;
  }

  if (kind === 'image') {
    // webp + large: more source pixels + DCT-free compression so fine
    // parallel lines (helmet vents, grids) don't moiré on retina downscale.
    const imgUrl = url.includes('format=') ? url : url + (url.includes('?') ? '&' : '?') + 'format=webp&variant=large';
    return (
      <a href={url} target="_blank" rel="noopener noreferrer">
        <img src={imgUrl} alt="" className="inline-media-img" />
      </a>
    );
  }

  if (kind === 'audio') {
    return <AudioBlock src={url} />;
  }

  if (kind === 'video') {
    if (hlsUrl) {
      const poster = url + (url.includes('?') ? '&' : '?') + 'format=jpg&variant=thumbnail';
      return <HlsPlayer src={hlsUrl} poster={poster} />;
    }
    return <video controls src={url} className="inline-media-video" playsInline />;
  }

  return (
    <a href={url} target="_blank" rel="noopener noreferrer">
      {url}
    </a>
  );
}
